import styled from "styled-components"
import React, { useState, useEffect, useRef } from "react"
import ReactDOM from "react-dom"

// --- Styled Components (unchanged) ---

const MultiSelectItem = styled.div<{ selected: boolean }>`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  /* white-space: nowrap; */
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`

const OptionRow = styled.div<{ selected: boolean }>`
  padding: 4px 8px;
  border-bottom: 1px solid #ccc;
  background: ${({ selected, theme }) => (selected ? theme.primary : "transparent")};
  color: ${({ selected, theme }) => (selected ? theme.white : theme.primaryText)};
  cursor: pointer;
  box-sizing: border-box;
  &:hover {
    background: ${({ selected, theme }) => (selected ? theme.primaryDark : "#f2f2f2")};
  }
`

const OptionsContainer = styled.div`
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
`

// --- Portal Component with forwardRef ---

const DropdownPortal = React.forwardRef<HTMLDivElement, { children: React.ReactNode; position: { top: number; left: number; width: number } }>(
  ({ children, position }, ref) => {
    return ReactDOM.createPortal(
      <div
        ref={ref}
        data-portal="multiselect"
        style={{
          position: "absolute",
          top: position.top,
          left: position.left,
          width: position.width,
          zIndex: 9999,
        }}
      >
        {children}
      </div>,
      document.body,
    )
  },
)
DropdownPortal.displayName = "DropdownPortal"

// --- Main MultiSelectDropdown Component ---

export const MultiSelectDropdown: React.FC<{
  options: { value: string; label: string }[]
  selected: any
  onChange: (selected: string[]) => void
  placeholder: string
}> = ({ options, selected, onChange, placeholder }) => {
  const selectedArray = Array.isArray(selected) ? selected : []
  const [dropdownOpen, setDropdownOpen] = useState(false)

  // Ref for the trigger element
  const dropdownRef = useRef<HTMLDivElement>(null)
  // Ref for the portal container (the rendered options)
  const portalRef = useRef<HTMLDivElement>(null)

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })

  // Close dropdown when clicking outside both the trigger and the portal
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        (!portalRef.current || !portalRef.current.contains(e.target as Node))
      ) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleOutside)
    return () => document.removeEventListener("mousedown", handleOutside)
  }, [])

  // When dropdown opens, calculate its position relative to the viewport
  useEffect(() => {
    if (dropdownOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
  }, [dropdownOpen])

  // Toggle selection of an option
  const toggleOption = (val: string) => {
    if (selectedArray.includes(val)) {
      onChange(selectedArray.filter((s) => s !== val))
    } else {
      onChange([...selectedArray, val])
    }
  }

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <MultiSelectItem selected={selectedArray.length > 0} onClick={() => setDropdownOpen(!dropdownOpen)}>
        {selectedArray.length > 0 ? selectedArray.join(", ") : placeholder}
      </MultiSelectItem>
      {dropdownOpen && (
        <DropdownPortal position={dropdownPosition} ref={portalRef}>
          <OptionsContainer>
            {options.map((opt) => (
              <OptionRow key={opt.value} selected={selectedArray.includes(opt.value)} onClick={() => toggleOption(opt.value)}>
                {opt.label}
              </OptionRow>
            ))}
          </OptionsContainer>
        </DropdownPortal>
      )}
    </div>
  )
}
